import React, { useState, useEffect } from "react";
import {
  ArrowDown,
  ArrowUp,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Filter,
  List,
  Search,
  X,
} from "lucide-react";
import MainLayout from "../../components/layout/MainLayout";
import { useParams } from "react-router-dom";
import {
  getApprovalFormByProjectId,
  searchProjectLogs,
} from "../../../Services/approvalFormService";
import ScaffoldTableRow from "./components/scaffold-table-row";
import { Box, Button } from "@mui/material";
import { t } from "../../../utils/translate";

export default function ProjectLog() {
  const { projectId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [scaffoldData, setScaffoldData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [scaffoldStats, setScaffoldStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    disassembled: 0,
  });
  const [unitTotals, setUnitTotals] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });
  const [showLeftSidebar, setShowLeftSidebar] = useState(false);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  useEffect(() => {
    fetchScaffoldData();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm) {
        handleSearch(searchTerm);
      } else if (scaffoldData.length > 0) {
        applyFilters();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  useEffect(() => {
    if (scaffoldData.length > 0) {
      applyFilters();
    }
  }, [currentFilter, dateFilter, dateRange]);

  const fetchScaffoldData = async () => {
    try {
      setIsLoading(true);
      const response = await getApprovalFormByProjectId(projectId);
      const data = response?.data?.filter((item) => !item.isDeleted) || [];

      setScaffoldData(data);
      setFilteredData(data);

      const stats = {
        total: data.length,
        active: data.filter((item) => item.status === "active").length,
        inactive: data.filter((item) => item.status === "inactive").length,
        disassembled: data.filter((item) => item.status === "disassembled")
          .length,
      };

      setScaffoldStats(stats);

      const totals = calculateUnitTotals(data);
      setUnitTotals(totals);
    } catch (error) {
      console.error("Error fetching scaffold data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateUnitTotals = (data) => {
    const totals = { m2: 0, m3: 0, lm: 0, hm: 0 };

    data.forEach((item) => {
      item.scaffoldName?.forEach((scaffold) => {
        if (scaffold.measurements) {
          scaffold.measurements.m2?.forEach((measurement) => {
            const length = Number.parseFloat(measurement.length) || 0;
            const width = Number.parseFloat(measurement.width) || 0;
            totals.m2 += length * width;
          });

          scaffold.measurements.m3?.forEach((measurement) => {
            const length = Number.parseFloat(measurement.length) || 0;
            const width = Number.parseFloat(measurement.width) || 0;
            const height = Number.parseFloat(measurement.height) || 0;
            totals.m3 += length * width * height;
          });

          scaffold.measurements.lm?.forEach((measurement) => {
            const length = Number.parseFloat(measurement.length) || 0;
            totals.lm += length;
          });

          scaffold.measurements.hm?.forEach((measurement) => {
            const height = Number.parseFloat(measurement.height) || 0;
            totals.hm += height;
          });
        }
      });
    });

    return totals;
  };

  const applyFilters = () => {
    let result = [...scaffoldData];

    if (currentFilter !== "all") {
      result = result.filter((item) => item.status === currentFilter);
    }

    if (dateFilter) {
      const now = new Date();
      let startDate;

      switch (dateFilter) {
        case "day":
          startDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case "week":
          startDate = new Date(now);
          startDate.setDate(now.getDate() - now.getDay());
          break;
        case "month":
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case "year":
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        result = result.filter((item) => new Date(item.date) >= startDate);
      }
    }

    if (dateRange.start && dateRange.end) {
      const start = new Date(dateRange.start);
      const end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);

      result = result.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= start && itemDate <= end;
      });
    }

    result = sortData(result, sortConfig.key, sortConfig.direction);

    setFilteredData(result);

    const totals = calculateUnitTotals(result);
    setUnitTotals(totals);
  };

  const handleSearch = async (term) => {
    try {
      setIsLoading(true);
      const response = await searchProjectLogs(projectId, {
        searchTerm: term,
      });
      setFilteredData(response?.approvalForm || []);
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    setCurrentFilter(status);
    setCurrentPage(1);
  };

  const handleDateFilter = (filter) => {
    setDateFilter(filter);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (e, type) => {
    setDateRange((prev) => ({
      ...prev,
      [type]: e.target.value,
    }));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    const sorted = sortData([...filteredData], key, direction);
    setFilteredData(sorted);
  };

  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      if (key === "date") {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const openNewTab = () => {
    window.open(`/volume/${projectId}`, "_blank");
  };

  const openNewTabRent = () => {
    window.open(`/rental/${projectId}`, "_blank");
  };

  const openPriceTab = () => {
    window.open(`/price/${projectId}`, "_blank");
  };

  return (
    <MainLayout title="Project Log">
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold mb-6">Scaffold Management</h1>

          {/* Search and Date Range */}
          <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
            <div className="relative w-full md:w-1/2">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-gray-500" />
              </div>
              <input
                type="search"
                className="block w-full p-3 pl-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Search scaffolds..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <div className="flex flex-col md:flex-row gap-2">
                <input
                  type="date"
                  className="p-2 text-sm border border-gray-300 rounded-lg"
                  onChange={(e) => handleDateRangeChange(e, "start")}
                  value={dateRange.start || ""}
                />
                <input
                  type="date"
                  className="p-2 text-sm border border-gray-300 rounded-lg"
                  onChange={(e) => handleDateRangeChange(e, "end")}
                  value={dateRange.end || ""}
                />
              </div>

              {isMobileView && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowLeftSidebar(!showLeftSidebar)}
                    className="p-2 bg-white border border-gray-300 rounded-lg"
                  >
                    <Filter className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowRightSidebar(!showRightSidebar)}
                    className="p-2 bg-white border border-gray-300 rounded-lg"
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 overflow-auto">
            {/* Left Sidebar - Filters */}
            <div
              className={`
            ${
              isMobileView
                ? `fixed inset-0 z-50 bg-white p-4 transform ${
                    showLeftSidebar ? "translate-x-0" : "-translate-x-full"
                  } transition-transform duration-300 ease-in-out overflow-auto`
                : "w-64 bg-white rounded-lg shadow-md p-4"
            }
          `}
            >
              {isMobileView && (
                <button
                  onClick={() => setShowLeftSidebar(false)}
                  className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              )}

              <div className="bg-white p-2 rounded-lg cursor-pointer mb-3">
                <h2 className="font-semibold mb-3">
                  {t("priceManager")}
                </h2>

                <div className="flex flex-col gap-2">
                  <button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    className="whitespace-nowrap bg-[#0072BB] botton-text rounded-[5px] sm:px-[20px] px-[10px] sm:py-[10px] py-[10px] text-white h-[6%]"
                    onClick={openNewTab}
                  >
                    Volume Base
                  </button>

                  <button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    className="bg-[#0072BB] botton-text rounded-[5px] sm:px-[20px] px-[10px] sm:py-[10px] py-[10px] text-white h-[6%]"
                    onClick={openNewTabRent}
                  >
                    Rental Base
                  </button>

                  <button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    className="bg-[#0072BB] botton-text rounded-[5px] sm:px-[20px] px-[10px] sm:py-[10px] py-[10px] text-white h-[6%]"
                    onClick={openPriceTab}
                  >
                    Prices
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold mb-3">Status Filters</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      className="mr-2"
                      checked={currentFilter === "all"}
                      onChange={() => handleStatusFilter("all")}
                    />
                    <span>All</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      className="mr-2"
                      checked={currentFilter === "active"}
                      onChange={() => handleStatusFilter("active")}
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      className="mr-2"
                      checked={currentFilter === "inactive"}
                      onChange={() => handleStatusFilter("inactive")}
                    />
                    <span>Inactive</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      className="mr-2"
                      checked={currentFilter === "disassembled"}
                      onChange={() => handleStatusFilter("disassembled")}
                    />
                    <span>Disassembled</span>
                  </label>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3">Date Filters</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dateFilter"
                      className="mr-2"
                      checked={dateFilter === null}
                      onChange={() => handleDateFilter(null)}
                    />
                    <span>All Time</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dateFilter"
                      className="mr-2"
                      checked={dateFilter === "day"}
                      onChange={() => handleDateFilter("day")}
                    />
                    <span>Today</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dateFilter"
                      className="mr-2"
                      checked={dateFilter === "week"}
                      onChange={() => handleDateFilter("week")}
                    />
                    <span>This Week</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dateFilter"
                      className="mr-2"
                      checked={dateFilter === "month"}
                      onChange={() => handleDateFilter("month")}
                    />
                    <span>This Month</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dateFilter"
                      className="mr-2"
                      checked={dateFilter === "year"}
                      onChange={() => handleDateFilter("year")}
                    />
                    <span>This Year</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Main Content - Table */}
            <div className="flex-1">
              {isLoading ? (
                <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-md">
                  <div className="animate-pulse flex space-x-2">
                    <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                    <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                    <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                  </div>
                </div>
              ) : filteredData.length > 0 ? (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => handleSort("title")}
                          >
                            <div className="flex items-center">
                              Title
                              {sortConfig.key === "title" &&
                                (sortConfig.direction === "asc" ? (
                                  <ArrowUp className="ml-1 w-4 h-4" />
                                ) : (
                                  <ArrowDown className="ml-1 w-4 h-4" />
                                ))}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => handleSort("location")}
                          >
                            <div className="flex items-center">
                              Location
                              {sortConfig.key === "location" &&
                                (sortConfig.direction === "asc" ? (
                                  <ArrowUp className="ml-1 w-4 h-4" />
                                ) : (
                                  <ArrowDown className="ml-1 w-4 h-4" />
                                ))}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => handleSort("status")}
                          >
                            <div className="flex items-center">
                              Status
                              {sortConfig.key === "status" &&
                                (sortConfig.direction === "asc" ? (
                                  <ArrowUp className="ml-1 w-4 h-4" />
                                ) : (
                                  <ArrowDown className="ml-1 w-4 h-4" />
                                ))}
                            </div>
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                            onClick={() => handleSort("date")}
                          >
                            <div className="flex items-center">
                              Date
                              {sortConfig.key === "date" &&
                                (sortConfig.direction === "asc" ? (
                                  <ArrowUp className="ml-1 w-4 h-4" />
                                ) : (
                                  <ArrowDown className="ml-1 w-4 h-4" />
                                ))}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {currentItems.map((item) => (
                          <tr key={item._id} className="hover:bg-gray-50">
                            <ScaffoldTableRow item={item} />
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {item.location}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                              ${
                                item.status === "active"
                                  ? "bg-green-100 text-green-800"
                                  : item.status === "inactive"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                              >
                                {item.status.charAt(0).toUpperCase() +
                                  item.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(item.date)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === 1
                            ? "bg-gray-100 text-gray-400"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === totalPages
                            ? "bg-gray-100 text-gray-400"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing{" "}
                          <span className="font-medium">
                            {indexOfFirstItem + 1}
                          </span>{" "}
                          to{" "}
                          <span className="font-medium">
                            {indexOfLastItem > filteredData.length
                              ? filteredData.length
                              : indexOfLastItem}
                          </span>{" "}
                          of{" "}
                          <span className="font-medium">
                            {filteredData.length}
                          </span>{" "}
                          results
                        </p>
                      </div>
                      <div>
                        <nav
                          className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                          aria-label="Pagination"
                        >
                          <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                              currentPage === 1
                                ? "text-gray-300"
                                : "text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            <span className="sr-only">Previous</span>
                            <ChevronLeft className="h-5 w-5" />
                          </button>

                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter((page) => {
                              // Show first page, last page, current page, and pages around current page
                              return (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 &&
                                  page <= currentPage + 1)
                              );
                            })
                            .map((page, i, array) => {
                              // Add ellipsis
                              const showEllipsisBefore =
                                i > 0 && array[i - 1] !== page - 1;
                              const showEllipsisAfter =
                                i < array.length - 1 &&
                                array[i + 1] !== page + 1;

                              return (
                                <React.Fragment key={page}>
                                  {showEllipsisBefore && (
                                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                      ...
                                    </span>
                                  )}

                                  <button
                                    onClick={() => paginate(page)}
                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                      currentPage === page
                                        ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                    }`}
                                  >
                                    {page}
                                  </button>

                                  {showEllipsisAfter && (
                                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                      ...
                                    </span>
                                  )}
                                </React.Fragment>
                              );
                            })}

                          <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                              currentPage === totalPages
                                ? "text-gray-300"
                                : "text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            <span className="sr-only">Next</span>
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 bg-white rounded-lg shadow-md">
                  <Calendar className="w-12 h-12 text-gray-400 mb-2" />
                  <p className="text-gray-500">No scaffold data found</p>
                </div>
              )}
            </div>

            {/* Right Sidebar - Stats */}
            <div
              className={`
            ${
              isMobileView
                ? `fixed inset-0 z-50 bg-white p-4 transform ${
                    showRightSidebar ? "translate-x-0" : "translate-x-full"
                  } transition-transform duration-300 ease-in-out overflow-auto`
                : "w-64 bg-white rounded-lg shadow-md p-4"
            }
          `}
            >
              {isMobileView && (
                <button
                  onClick={() => setShowRightSidebar(false)}
                  className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              )}

              <div className="mb-6">
                <h3 className="font-semibold mb-3">Scaffold Status</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    className="bg-gray-100 p-3 rounded-lg text-center cursor-pointer hover:bg-gray-200"
                    onClick={() => handleStatusFilter("all")}
                  >
                    <div className="text-xs text-gray-500">Total</div>
                    <div className="font-semibold text-lg">
                      {scaffoldStats.total}
                    </div>
                  </div>
                  <div
                    className="bg-green-100 p-3 rounded-lg text-center cursor-pointer hover:bg-green-200"
                    onClick={() => handleStatusFilter("active")}
                  >
                    <div className="text-xs text-gray-500">Active</div>
                    <div className="font-semibold text-lg">
                      {scaffoldStats.active}
                    </div>
                  </div>
                  <div
                    className="bg-red-100 p-3 rounded-lg text-center cursor-pointer hover:bg-red-200"
                    onClick={() => handleStatusFilter("inactive")}
                  >
                    <div className="text-xs text-gray-500">Inactive</div>
                    <div className="font-semibold text-lg">
                      {scaffoldStats.inactive}
                    </div>
                  </div>
                  <div
                    className="bg-yellow-100 p-3 rounded-lg text-center cursor-pointer hover:bg-yellow-200"
                    onClick={() => handleStatusFilter("disassembled")}
                  >
                    <div className="text-xs text-gray-500">Disassembled</div>
                    <div className="font-semibold text-lg">
                      {scaffoldStats.disassembled}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3">Unit Measurements</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <div className="text-xs text-gray-500">M²</div>
                    <div className="font-semibold">
                      {Math.round(unitTotals.m2 * 100) / 100 || 0}
                    </div>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <div className="text-xs text-gray-500">M³</div>
                    <div className="font-semibold">
                      {Math.round(unitTotals.m3 * 100) / 100 || 0}
                    </div>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <div className="text-xs text-gray-500">LM</div>
                    <div className="font-semibold">
                      {Math.round(unitTotals.lm * 100) / 100 || 0}
                    </div>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-lg text-center">
                    <div className="text-xs text-gray-500">HM</div>
                    <div className="font-semibold">
                      {Math.round(unitTotals.hm * 100) / 100 || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
